/**
 * The Response panel on alert detail (Story 19, design §9.7): one button per
 * action the alert offers, disabled with the reason when it does not; a
 * confirmation that names the target, the provider and every warning as an
 * acknowledgment; the action records with their state and a Release; the
 * withheld entries at equal weight.
 *
 * Everything here is data-driven from `available_actions`, `actions` and
 * `withheld`. The SPA never names an action: the lint that keeps every
 * action name inside the response layer scans this tree, and a label is
 * always derived from the name the API sent.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  ApiError,
  releaseAction,
  requestAction,
  type ActionRecord,
  type ActionRequestResponse,
  type AvailableAction,
  type RailResult,
  type WithheldEntry,
} from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { useMdrToken } from './context.js';
import { actionLabel, timeAgo } from './format.js';
import { useBoundedPoll } from './useBoundedPoll.js';
import { ErrorNote, INPUT, ModalShell } from './ModalShell.js';

// A test record executed nothing at the provider, so there is nothing to
// release; offering one read as if it had (task 108). The API still accepts
// a test release, so the rehearsal path stays reachable outside the SPA.
const RELEASABLE = new Set(['succeeded', 'partial']);
const IN_FLIGHT = new Set(['requested', 'running']);

type Pending =
  | { kind: 'action'; offer: AvailableAction }
  | { kind: 'release'; record: ActionRecord; warnings: RailResult[] };

export function ResponsePanel({
  tenantId,
  alertRef,
  detailKey,
  available,
  actions,
  withheld,
  canRequest,
}: {
  tenantId: string;
  alertRef: string;
  detailKey: unknown[];
  available: AvailableAction[];
  actions: ActionRecord[];
  withheld: WithheldEntry[];
  canRequest: boolean;
}) {
  const idToken = useMdrToken();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Pending | null>(null);
  const [outcome, setOutcome] = useState<ActionRequestResponse | null>(null);

  // 3-second polling while a record is in flight (design §9.7 UI), bounded.
  useBoundedPoll(actions.some((a) => IN_FLIGHT.has(a.status)), detailKey);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: detailKey });
  const mutation = useMutation<
    ActionRequestResponse,
    Error,
    { pending: Pending; note: string; acknowledge: string[] }
  >({
    mutationFn: ({ pending: p, note, acknowledge }) =>
      p.kind === 'action'
        ? requestAction(
            tenantId,
            alertRef,
            {
              action: p.offer.action,
              target: {
                asset_type: p.offer.target.asset_type,
                natural_key: p.offer.target.natural_key,
              },
              note: note || undefined,
              acknowledge,
            },
            idToken,
          )
        : releaseAction(
            tenantId,
            alertRef,
            p.record.action_id,
            { note: note || undefined, acknowledge },
            idToken,
          ),
    onSuccess: (data) => {
      setPending(null);
      setOutcome(data);
      refresh();
    },
    onError: (error) => {
      // A 409 is "acknowledge the warnings" or "not releasable any more";
      // both are answered by the fresh detail.
      if (error instanceof ApiError && error.status === 409) refresh();
    },
  });

  const hasAnything = available.length > 0 || actions.length > 0 || withheld.length > 0;
  if (!hasAnything) return null;

  return (
    <div className="mt-4 border-t border-bluesphere-border pt-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-bluesphere-muted">
          Response
        </h2>
        {available.some((o) => o.test_mode) && (
          <span className="text-xs text-bluesphere-muted">
            Test mode on this Client: requests are recorded, nothing executes.
          </span>
        )}
      </div>

      {available.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {available.map((offer) => {
            const key = `${offer.action}:${offer.target.asset_type}:${offer.target.natural_key}`;
            const label = `${actionLabel(offer.action)} · ${offer.target.display_name}`;
            const busy = actions.some(
              (a) =>
                IN_FLIGHT.has(a.status) &&
                a.target.natural_key === offer.target.natural_key &&
                a.action === offer.action,
            );
            const disabled = !canRequest || !offer.available || busy;
            const title = !canRequest
              ? 'Your role can view response actions but not request them.'
              : offer.reason ?? (busy ? 'Already in progress.' : `${offer.provider}: ${offer.target.provider_ref}`);
            return (
              <span key={key} title={title} className="inline-flex">
                <PillButton
                  size="sm"
                  variant={offer.emphasis === 'secondary' ? 'secondary' : 'danger'}
                  disabled={disabled}
                  onClick={() => setPending({ kind: 'action', offer })}
                >
                  {label}
                </PillButton>
              </span>
            );
          })}
        </div>
      )}
      {available.length > 0 && available.every((o) => !o.available) && (
        <ul className="mt-2 space-y-0.5 text-xs text-bluesphere-muted">
          {available.map((o) => (
            <li key={`${o.action}:${o.target.natural_key}`}>
              {actionLabel(o.action)} on {o.target.display_name}: {o.reason}
            </li>
          ))}
        </ul>
      )}

      {outcome && (
        <div
          className={`mt-3 rounded-input border px-3 py-2 text-xs ${
            outcome.outcome === 'withheld'
              ? 'border-amber-300 bg-amber-50 text-amber-900'
              : 'border-bluesphere-border bg-bluesphere-surfaceMuted text-bluesphere-textSecondary'
          }`}
        >
          {outcome.outcome === 'withheld'
            ? `Withheld: ${outcome.withheld?.blocked_by.map(actionLabel).join(', ')}. Recorded on the alert.`
            : outcome.action?.rails.test_mode
              ? 'Recorded. Test mode: the rails were evaluated and nothing executed.'
              : 'Requested. The outcome appears below as the provider reports it.'}
        </div>
      )}

      {(actions.length > 0 || withheld.length > 0) && (
        <ul className="mt-4 divide-y divide-bluesphere-border">
          {[...actions.map((a) => ({ kind: 'action' as const, at: a.requested_at, a })),
            ...withheld.map((w) => ({ kind: 'withheld' as const, at: w.at, w }))]
            .sort((x, y) => (x.at < y.at ? 1 : -1))
            .map((row) =>
              row.kind === 'action' ? (
                <ActionRow
                  key={row.a.action_id}
                  record={row.a}
                  canRequest={canRequest}
                  onRelease={(record) =>
                    setPending({
                      kind: 'release',
                      record,
                      warnings: available.find(
                        (o) => o.target.natural_key === record.target.natural_key,
                      )?.warnings ?? [],
                    })
                  }
                />
              ) : (
                <WithheldRow key={row.w.withheld_id} entry={row.w} />
              ),
            )}
        </ul>
      )}

      {pending && (
        <ConfirmActionModal
          pending={pending}
          isPending={mutation.isPending}
          error={mutation.error}
          onCancel={() => {
            setPending(null);
            mutation.reset();
          }}
          onConfirm={(note, acknowledge) => mutation.mutate({ pending, note, acknowledge })}
        />
      )}
    </div>
  );
}

// ----- Rows -------------------------------------------------------------------

const STATUS_STYLES: Record<string, { label: string; classes: string }> = {
  requested: { label: 'Requested', classes: 'bg-amber-100 text-amber-800 border-amber-300' },
  running: { label: 'Running', classes: 'bg-amber-100 text-amber-800 border-amber-300' },
  succeeded: { label: 'Succeeded', classes: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  partial: { label: 'Partial', classes: 'bg-amber-100 text-amber-800 border-amber-300' },
  failed: { label: 'Failed', classes: 'bg-red-100 text-red-800 border-red-300' },
  test: { label: 'Test', classes: 'bg-sky-100 text-sky-800 border-sky-300' },
  withheld: {
    label: 'Withheld',
    classes: 'bg-bluesphere-surfaceMuted text-bluesphere-textSecondary border-bluesphere-borderMuted',
  },
};

export function ActionStatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[status] ?? {
    label: actionLabel(status),
    classes: 'bg-white text-bluesphere-textSecondary border-bluesphere-borderMuted',
  };
  return (
    <span
      className={`rounded-sm border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${s.classes}`}
    >
      {s.label}
    </span>
  );
}

function requesterLabel(r: ActionRecord['requested_by']): string {
  if (r.kind === 'rule') {
    const version = r.version ? ` (v${r.version})` : '';
    return `playbook ${r.playbook_id ?? ''} · rule ${r.rule_id ?? ''}${version}`;
  }
  return r.email || r.user_id || r.sub || 'operator';
}

function ActionRow({
  record,
  canRequest,
  onRelease,
}: {
  record: ActionRecord;
  canRequest: boolean;
  onRelease: (record: ActionRecord) => void;
}) {
  const releasable = canRequest && !record.release_of && RELEASABLE.has(record.status);
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-3 text-sm">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <ActionStatusBadge status={record.status} />
          <span className="font-medium text-bluesphere-textOnLight">
            {actionLabel(record.action)}
            {record.release_of && ' (release)'}
          </span>
          <span className="text-bluesphere-textSecondary">{record.target.display_name}</span>
          <span className="text-xs text-bluesphere-muted">via {record.provider}</span>
        </div>
        <div className="mt-0.5 text-xs text-bluesphere-muted" title={record.requested_at}>
          {requesterLabel(record.requested_by)} · {timeAgo(record.requested_at)}
          {record.finished_at && ` · finished ${timeAgo(record.finished_at)}`}
          {record.operation_id && ` · ${record.provider} ref ${record.operation_id}`}
        </div>
        {record.rails.acknowledged.length > 0 && (
          <div className="text-xs text-bluesphere-muted">
            acknowledged: {record.rails.acknowledged.map(actionLabel).join(', ')}
          </div>
        )}
        {record.rails.test_mode && record.rails.would_block.length > 0 && (
          <div className="text-xs text-amber-800">
            live would have been withheld by: {record.rails.would_block.map(actionLabel).join(', ')}
          </div>
        )}
        {record.error && <div className="text-xs text-red-700">{record.error}</div>}
        {record.note && (
          <div className="wrap-break-word text-xs text-bluesphere-textSecondary">“{record.note}”</div>
        )}
      </div>
      {releasable && (
        <span
          title={`Releases ${actionLabel(record.action).toLowerCase()} on ${record.target.display_name}.`}
          className="inline-flex"
        >
          <PillButton size="sm" variant="secondary" onClick={() => onRelease(record)}>
            {record.release ? actionLabel(record.release) : 'Release'}
          </PillButton>
        </span>
      )}
    </li>
  );
}

function WithheldRow({ entry }: { entry: WithheldEntry }) {
  return (
    <li className="py-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <ActionStatusBadge status="withheld" />
        <span className="font-medium text-bluesphere-textOnLight">
          {actionLabel(entry.action)}
          {entry.release_of && ' (release)'}
        </span>
        {entry.target.display_name && (
          <span className="text-bluesphere-textSecondary">{entry.target.display_name}</span>
        )}
        {entry.escalated && (
          <span
            title="Automatic response was withheld; the rule cannot act on this alert."
            className="rounded-sm border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800"
          >
            Operator must decide
          </span>
        )}
      </div>
      <div className="mt-0.5 text-xs text-bluesphere-textOnLight">
        stopped by {entry.blocked_by.map(actionLabel).join(', ')}
      </div>
      <ul className="mt-0.5 space-y-0.5 text-xs text-bluesphere-muted">
        {entry.rails.evaluated
          .filter((r) => entry.blocked_by.includes(r.rail))
          .map((r) => (
            <li key={r.rail}>{r.detail}</li>
          ))}
      </ul>
      <div className="text-xs text-bluesphere-muted" title={entry.at}>
        {requesterLabel(entry.requested_by)} · {timeAgo(entry.at)}
      </div>
    </li>
  );
}

// ----- The confirmation ---------------------------------------------------------

function ConfirmActionModal({
  pending,
  isPending,
  error,
  onCancel,
  onConfirm,
}: {
  pending: Pending;
  isPending: boolean;
  error: unknown;
  onCancel: () => void;
  onConfirm: (note: string, acknowledge: string[]) => void;
}) {
  const warnings = pending.kind === 'action' ? pending.offer.warnings : pending.warnings;
  const target = pending.kind === 'action' ? pending.offer.target : pending.record.target;
  const provider = pending.kind === 'action' ? pending.offer.provider : pending.record.provider;
  const verb =
    pending.kind === 'action'
      ? actionLabel(pending.offer.action)
      : pending.record.release
        ? actionLabel(pending.record.release)
        : 'Release';
  const testMode = pending.kind === 'action' ? pending.offer.test_mode : pending.record.rails.test_mode;
  const [acked, setAcked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const allAcked = warnings.every((w) => acked.has(w.rail));

  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          {verb} {target.display_name}?
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          Through <strong>{provider}</strong> on{' '}
          <span className="font-mono text-xs">{target.provider_ref}</span>.{' '}
          {testMode
            ? 'This Client is in test mode: the request is evaluated and recorded; nothing reaches the provider.'
            : pending.kind === 'release'
              ? 'The provider is asked to undo the earlier action; the outcome is recorded on the alert.'
              : 'The provider is asked to act; the outcome is tracked to completion and recorded on the alert.'}
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (allAcked && !isPending) onConfirm(note.trim(), [...acked]);
        }}
        className="space-y-4 px-6 py-5"
      >
        {warnings.length > 0 && (
          <fieldset className="space-y-2">
            <legend className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
              Acknowledge each warning
            </legend>
            {warnings.map((w) => {
              // The action's own caution (`action_caution`, r20) reads as a
              // strong warning, not one more rail row.
              const caution = w.rail === 'action_caution';
              return (
                <label
                  key={w.rail}
                  className={`flex cursor-pointer items-start gap-3 text-sm ${
                    caution ? 'rounded-input border border-red-300 bg-red-50 px-3 py-2' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={acked.has(w.rail)}
                    onChange={(e) => {
                      const next = new Set(acked);
                      if (e.target.checked) next.add(w.rail);
                      else next.delete(w.rail);
                      setAcked(next);
                    }}
                    className="mt-1 h-4 w-4 rounded-sm border-bluesphere-borderMuted"
                  />
                  <span>
                    <span
                      className={
                        caution
                          ? 'font-semibold text-red-800'
                          : 'font-medium text-bluesphere-textOnLight'
                      }
                    >
                      {caution ? 'Warning' : actionLabel(w.rail)}
                    </span>
                    <span
                      className={`block text-xs ${
                        caution ? 'text-red-900' : 'text-bluesphere-textSecondary'
                      }`}
                    >
                      {w.detail}
                    </span>
                  </span>
                </label>
              );
            })}
          </fieldset>
        )}
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            Note (optional, recorded with the action)
          </span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            className={INPUT}
          />
        </label>
        <ErrorNote error={error} />
        <div className="flex items-center justify-end gap-2 pt-1">
          <PillButton variant="ghost" size="sm" type="button" onClick={onCancel}>
            Cancel
          </PillButton>
          <PillButton
            size="sm"
            type="submit"
            variant={pending.kind === 'release' ? 'secondary' : 'danger'}
            disabled={!allAcked || isPending}
          >
            {isPending ? 'Requesting…' : testMode ? `${verb} (test)` : verb}
          </PillButton>
        </div>
      </form>
    </ModalShell>
  );
}
